import { useMutation, useQuery } from '@apollo/client'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Card } from '@/components_deprecated/Card'
import { format, parseISO } from 'date-fns'
import React from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import theme from '@/theme'
import { HIDE_WEIRD_ACTIVITY_NOTIFICATION } from './mutations'
import { DASHBOARD_NOTIFICATIONS_QUERY } from './queries'

const Wrapper = styled.section`
  max-width: 100%;
`

const NotificationWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 1.1rem;
  line-height: 1.5rem;
`

const ListCard = styled(Card)`
  display: flex;
  flex-direction: column;
  min-width: min-content;
  height: 100%;

  overflow: hidden;
  background: white;
  gap: 0.5rem;
  padding: 2rem;

  a {
    color: black;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`

interface DashboardNotificationsProps {}

export const DashboardNotifications: React.FC<
  DashboardNotificationsProps
> = () => {
  const translations = useTranslate({
    title: 'common.notifications-alt',
    expiredCard: ['users.n-has-one-or-more-expired-driver-cards', { n: '' }],
    noNotifications: 'notifications.you-have-no-notifications',
    weirdDriverActivityNoticedForNOnDate: [
      'users.weird-driver-activity-noticed-for-n-on-date',
      { n: '', date: '' },
    ],
  })

  const { data, loading } = useQuery(DASHBOARD_NOTIFICATIONS_QUERY)

  const nodes =
    data?.weirdActivityNotificationGroups.filter(group => !!group) || []

  const [hideWeirdActivityNotification] = useMutation(
    HIDE_WEIRD_ACTIVITY_NOTIFICATION,
    {
      refetchQueries: [
        {
          query: DASHBOARD_NOTIFICATIONS_QUERY,
        },
      ],
    }
  )
  const handleHideNotification = async (
    userId: string,
    timestampGte: string,
    timestampLt: string
  ) => {
    await hideWeirdActivityNotification({
      variables: {
        userId,
        timestampGte,
        timestampLt,
      },
    })
  }

  return (
    <Wrapper>
      <header>
        <h2>{translations.title}</h2>
      </header>

      <ListCard>
        {nodes.map(notificationGroup => {
          const notificationStartDate = parseISO(notificationGroup.periodStart)
          const notificationEndDate = parseISO(notificationGroup.periodEnd)
          const isSameDate =
            format(notificationStartDate, 'yyyy-MM-dd') ===
            format(notificationEndDate, 'yyyy-MM-dd')
          const displayDate = isSameDate
            ? format(notificationStartDate, 'dd.MM.yyyy')
            : `${format(notificationStartDate, 'dd.MM.yyyy HH:mm')} - ${format(
                notificationEndDate,
                'dd.MM.yyyy HH:mm'
              )}`

          return (
            <NotificationWrapper key={notificationGroup.user.id}>
              <Icon
                icon="exclamation-triangle"
                color={theme.colors.orange}
                size="1.1rem"
                margin="2px 0 0 0"
              />
              <Link
                to={`/activities/${notificationGroup.user.id}/${format(
                  notificationStartDate,
                  'yyyy-MM-dd'
                )}`}
              >
                {translations.weirdDriverActivityNoticedForNOnDate({
                  n: notificationGroup.user.fullName,
                  date: displayDate,
                })}
              </Link>
              <Icon
                icon="times"
                size="1.1rem"
                margin="2px 0 0 0"
                hoverColor="red"
                cursor="pointer"
                onClick={evt => {
                  evt.preventDefault()
                  evt.stopPropagation()
                  void handleHideNotification(
                    notificationGroup.user.id,
                    notificationGroup.periodStart,
                    notificationGroup.periodEnd
                  )
                }}
              />
            </NotificationWrapper>
          )
        })}
        {!loading && !nodes.length && <p>{translations.noNotifications}.</p>}
      </ListCard>
    </Wrapper>
  )
}
